<template>
  <div class="song-management">
    <div class="upload-warning">
      只有原创或经过授权的音乐才能在这里闪光 ✨。请勿上传侵权内容，否则由此引发的法律后果需由您独自承担哦。
    </div>

    <!-- 搜索和操作栏 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="8">
        <el-input
          v-model="searchQuery"
          placeholder="请输入歌曲名称或艺术家"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </template>
        </el-input>
      </el-col>
      <el-col :span="4">
        <el-button type="primary" @click="showUploadDialog = true">
          <el-icon><Upload /></el-icon>
          上传音乐
        </el-button>
      </el-col>
    </el-row>

    <!-- 歌曲列表 -->
    <el-table
      :data="songs"
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa' }"
    >
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column prop="title" label="歌曲名称" min-width="130" show-overflow-tooltip />
      <el-table-column prop="artist" label="艺术家" min-width="100" show-overflow-tooltip />
      <el-table-column
        v-if="isAdmin"
        prop="filename"
        label="文件名"
        min-width="160"
        show-overflow-tooltip
      >
        <template #default="scope">
          {{ scope.row.filename || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="file_size" label="文件大小" width="90">
        <template #default="scope">
          {{ formatFileSize(scope.row.file_size) }}
        </template>
      </el-table-column>
      <el-table-column prop="modified_time" label="修改时间" width="140">
        <template #default="scope">
          {{ formatTime(scope.row.modified_time) }}
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="上传时间" width="140" />
      <el-table-column prop="owner_name" label="所有者" width="90" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.owner_id === 1 || scope.row.owner_name === 'admin' ? '平台' : (scope.row.owner_name || '未知用户') }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="scope">
          <!-- 只有管理员或歌曲所有者可以编辑 -->
          <el-button 
            size="small"
            type="primary"
            @click="onEdit(scope.row)"
            v-if="canEditSong(scope.row)"
          >编辑</el-button>
          <!-- 只有管理员或歌曲所有者可以删除 -->
          <el-button
            size="small"
            type="danger"
            @click="onDelete(scope.row)"
            v-if="canDeleteSong(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      style="margin-top: 20px; text-align: right;"
      background
      layout="prev, pager, next, jumper, ->, total"
      :total="total"
      :page-size="pageSize"
      v-model:current-page="currentPage"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑歌曲弹窗 -->
    <el-dialog
      :title="editMode ? '编辑歌曲' : '新增歌曲'"
      v-model="showEditDialog"
      width="600px"
    >
      <el-form
        ref="songForm"
        :model="editForm"
        :rules="songRules"
        label-width="80px"
      >
        <el-form-item label="歌曲名称" prop="title">
          <el-input v-model="editForm.title" placeholder="请输入歌曲名称" />
        </el-form-item>
        <el-form-item label="艺术家" prop="artist">
          <el-input v-model="editForm.artist" placeholder="请输入艺术家名称" />
        </el-form-item>
        <el-form-item label="所有者" prop="owner_id">
          <template v-if="isAdmin">
            <el-select
              v-model="editForm.owner_id"
              placeholder="请选择所有者"
              filterable
              clearable
            >
              <el-option
                v-for="user in users"
                :key="user.id"
                :label="user.username"
                :value="Number(user.id)"
              />
            </el-select>
          </template>
          <template v-else>
            <el-input :model-value="currentUser.username || ''" disabled />
          </template>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>

    <!-- 上传音乐弹窗 -->
    <el-dialog
      v-model="showUploadDialog"
      title="上传音乐文件"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="upload-warning">
        只有原创或经过授权的音乐才能在这里闪光 ✨。请勿上传侵权内容，否则由此引发的法律后果需由您独自承担哦。
      </div>
      <el-upload
        ref="uploadRef"
        :auto-upload="false"
        :on-change="handleFileChange"
        :before-upload="beforeUpload"
        :file-list="fileList"
        accept=".mp3,.wav,.flac,.m4a,.lrc"
        drag
        multiple
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">
          将文件拖到此处，或<em>点击上传</em>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            支持 mp3, wav, flac, m4a, lrc 格式，单个文件不超过 100MB
          </div>
        </template>
      </el-upload>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showUploadDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="uploading"
            @click="handleUpload"
          >
            上传
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getSongs, createSong, updateSong, deleteSong, uploadSongFile } from '../api/song';
import { getUsers } from '../api/auth';
import { Upload, UploadFilled } from '@element-plus/icons-vue';

export default {
  name: 'SongManagement',
  components: {
    Upload,
    UploadFilled
  },
  setup() {
    const songs = ref([]);
    const users = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const showEditDialog = ref(false);
    const editMode = ref(false);
    const showUploadDialog = ref(false);
    const uploading = ref(false);
    const fileList = ref([]);
    const uploadRef = ref();
    
    // 当前用户信息
    const currentUser = ref({
      username: '',
      role: '',
      id: null
    });

    const editForm = reactive({
      id: null,
      title: '',
      artist: '',
      owner_id: null
    });

    const songRules = {
      title: [
        { required: true, message: '请输入歌曲名称', trigger: 'blur' }
      ],
      artist: [
        { required: true, message: '请输入艺术家名称', trigger: 'blur' }
      ],
      owner_id: [
        { required: true, message: '请选择所有者', trigger: 'change' }
      ]
    };

    const songForm = ref();
    
    // 权限检查方法
    const canEditSong = (song) => {
      // 管理员可以编辑所有歌曲
      if (currentUser.value.role === 'admin') {
        return true;
      }
      // 普通用户只能编辑自己的歌曲
      return song.owner_id === currentUser.value.id;
    };
    
    const canDeleteSong = (song) => {
      // 管理员可以删除所有歌曲
      if (currentUser.value.role === 'admin') {
        return true;
      }
      // 普通用户只能删除自己的歌曲
      return song.owner_id === currentUser.value.id;
    };
    
    // 获取当前用户信息
    const getCurrentUser = () => {
      let user = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr && userStr !== 'undefined') {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        user = {};
      }
      currentUser.value = {
        username: user.username,
        role: user.role,
        id: user.id != null ? Number(user.id) : null
      };
    };
    
    // 计算属性：是否为管理员
    const isAdmin = computed(() => currentUser.value.role === 'admin');

    const loadSongs = async () => {
      try {
        loading.value = true;
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        if (searchQuery.value) {
          params.query = searchQuery.value;
        }
        
        const response = await getSongs(params);
        songs.value = response.data || [];
        total.value = response.total || 0;
      } catch (error) {
        console.error('加载歌曲列表失败:', error);
        ElMessage.error('加载歌曲列表失败');
      } finally {
        loading.value = false;
      }
    };


    const loadUsers = async () => {
      try {
        const response = await getUsers();
        users.value = response.data || [];
      } catch (error) {
        console.error('加载用户列表失败:', error);
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      loadSongs();
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      loadSongs();
    };

    const onEdit = (song) => {
      // 检查编辑权限
      if (!canEditSong(song)) {
        ElMessage.warning('您没有权限编辑此歌曲');
        return;
      }
      
      editMode.value = true;
      Object.assign(editForm, {
        id: song.id,
        title: song.title,
        artist: song.artist,
        owner_id: song.owner_id
      });
      showEditDialog.value = true;
    };

    const onDelete = async (song) => {
      // 检查删除权限
      if (!canDeleteSong(song)) {
        ElMessage.warning('您没有权限删除此歌曲');
        return;
      }
      
      try {
        await ElMessageBox.confirm(
          `确定要删除歌曲 "${song.title}" 吗？`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        const response = await deleteSong(song.id);
        if (response.success) {
          ElMessage.success('删除成功');
          loadSongs();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          ElMessage.error('删除失败');
        }
      }
    };

    const handleSave = async () => {
      try {
        await songForm.value.validate();
        saving.value = true;
        
        // 如果是普通用户，自动设置为自己的歌曲
        if (!isAdmin.value) {
          editForm.owner_id = currentUser.value.id;
        }

        const response = editMode.value 
          ? await updateSong(editForm.id, editForm)
          : await createSong(editForm);
          
        if (response.success) {
          ElMessage.success(editMode.value ? '更新成功' : '添加成功');
          showEditDialog.value = false;
          loadSongs();
        } else {
          ElMessage.error(response.message || (editMode.value ? '更新失败' : '添加失败'));
        }
      } catch (error) {
        console.error('保存失败:', error);
        ElMessage.error('保存失败');
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      editMode.value = false;
      Object.assign(editForm, {
        id: null,
        title: '',
        artist: '',
        owner_id: isAdmin.value ? null : currentUser.value.id
      });
      if (songForm.value) {
        songForm.value.resetFields();
      }
    };

    // 上传相关逻辑
    const beforeUpload = (file) => {
      // 检查文件扩展名（支持音频和歌词文件）
      const fileName = file.name.toLowerCase();
      const isAudioFile = fileName.endsWith('.mp3') || fileName.endsWith('.wav') || 
                         fileName.endsWith('.flac') || fileName.endsWith('.m4a');
      const isLyricFile = fileName.endsWith('.lrc');
      
      // 优先检查文件扩展名，因为 file.type 可能不准确
      const isValidType = isAudioFile || isLyricFile;

      if (!isValidType) {
        ElMessage.error('只能上传 mp3, wav, flac, m4a, lrc 格式的文件!');
        return false;
      }
      
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        ElMessage.error('文件大小不能超过 100MB!');
        return false;
      }
      return true;
    };

    const handleFileChange = (file, files) => {
      fileList.value = files;
    };

    const handleUpload = async () => {
      if (fileList.value.length === 0) {
        ElMessage.warning('请选择要上传的文件');
        return;
      }

      try {
        uploading.value = true;
        const files = [...fileList.value];
        const maxConcurrent = 5;
        const results = [];

        // 分批上传，每批最多 5 个文件，控制并发
        for (let i = 0; i < files.length; i += maxConcurrent) {
          const batch = files.slice(i, i + maxConcurrent);
          const batchPromises = batch.map(async (file) => {
            try {
              const result = await uploadSongFile(file.raw);
              return result;
            } catch (error) {
              // 处理单个文件上传失败
              const errorMessage = error.response?.data?.message || error.message || '上传失败';
              console.error(`文件 ${file.name} 上传失败:`, error);
              return {
                success: false,
                message: errorMessage,
                filename: file.name
              };
            }
          });
          const batchResults = await Promise.all(batchPromises);
          results.push(...batchResults);
        }

        const successCount = results.filter(result => result.success).length;
        const failedResults = results.filter(result => !result.success);

        if (successCount > 0) {
          ElMessage.success(`成功上传 ${successCount} 个文件`);
          if (failedResults.length > 0) {
            // 显示失败的文件信息
            failedResults.forEach(result => {
              ElMessage.warning(`${result.filename || '文件'}: ${result.message || '上传失败'}`);
            });
          }
          showUploadDialog.value = false;
          fileList.value = [];
          uploadRef.value?.clearFiles();
          loadSongs(); // 刷新歌曲列表
        } else {
          // 所有文件都失败了，显示第一个错误信息
          const firstError = failedResults[0];
          ElMessage.error(firstError?.message || '文件上传失败');
        }
      } catch (error) {
        console.error('上传异常:', error);
        ElMessage.error(error.response?.data?.message || error.message || '文件上传失败');
      } finally {
        uploading.value = false;
      }
    };

    // 格式化文件大小
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === '-') return '-';
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // 格式化时间
    const formatTime = (time) => {
      if (!time || time === '-') return '-';
      if (typeof time === 'string') {
        return time.replace('T', ' ').substring(0, 19);
      }
      return time;
    };

    onMounted(() => {
      getCurrentUser();
      loadSongs();
      // 只有管理员才需要加载用户列表（用于编辑歌曲时选择所有者）
      // 注意：getCurrentUser 是同步的，所以可以直接检查 isAdmin
      if (isAdmin.value) {
      loadUsers();
      }
    });

    return {
      songs,
      users,
      loading,
      saving,
      searchQuery,
      currentPage,
      pageSize,
      total,
      showEditDialog,
      editMode,
      showUploadDialog,
      uploading,
      fileList,
      uploadRef,
      currentUser,
      editForm,
      songRules,
      songForm,
      canEditSong,
      canDeleteSong,
      isAdmin,
      handleSearch,
      handlePageChange,
      onEdit,
      onDelete,
      handleSave,
      resetForm,
      beforeUpload,
      handleFileChange,
      handleUpload,
      formatFileSize,
      formatTime
    };
  }
};
</script>

<style scoped>
.song-management {
  padding: 20px;
}

.upload-warning {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #fff7e6;
  color: #d46b08;
  font-size: 13px;
  line-height: 1.6;
}
</style> 